/**
 * A small PDF writer for the board export (no dependency): A4 portrait pages with the standard fonts
 * Helvetica / Helvetica-Bold in WinAnsiEncoding (German umlauts and ß; characters outside it — emoji,
 * arrows … — are replaced or dropped), JPEG images (DCTDecode), lines, rectangles and link annotations
 * (/URI). Coordinates are in points from the top-left corner of the page; text is placed by its baseline.
 */

export type Rgb = [number, number, number];

export interface TextOpts {
  size?: number;
  bold?: boolean;
  color?: Rgb;
  align?: 'left' | 'center' | 'right';
}

export interface PdfImage { id: number; w: number; h: number; bytes: Uint8Array; }

interface Page { ops: string[]; links: { rect: [number, number, number, number]; uri: string }[]; images: Set<number>; }

/** Glyph widths (1/1000 em) of codes 32…255 in WinAnsiEncoding (Adobe's Helvetica AFM). */
const HELVETICA = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, 0,
  556, 0, 222, 556, 333, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0, 0, 222, 222, 333, 333, 350, 556, 1000, 333, 1000, 500, 333, 944, 0, 500, 667,
  278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278, 556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556, 500,
];
const HELVETICA_BOLD = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, 0,
  556, 0, 278, 556, 500, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0, 0, 278, 278, 500, 500, 350, 556, 1000, 333, 1000, 556, 333, 944, 0, 500, 667,
  278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278, 611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611, 556,
];

/** Unicode → WinAnsi codes 0x80…0x9F (the rest of 0xA0…0xFF is Latin-1 itself). */
const WIN_ANSI: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88,
  0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93,
  0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};
/** Characters outside WinAnsi with a readable stand-in. */
const SUBST: Record<string, string> = {
  '−': '-', '‐': '-', '‑': '-', '→': '->', '←': '<-', '↑': '^', '↓': 'v',
  '≈': '~', '≤': '<=', '≥': '>=', '≠': '!=', ' ': ' ', ' ': ' ', ' ': ' ',
  ' ': ' ', ' ': ' ', ' ': ' ', 'Σ': 'Sum', '∑': 'Sum', '′': "'", '″': '"',
};

/** Text → WinAnsi codes; unsupported characters (emoji …) are dropped, runs of spaces collapsed. */
export function winAnsi(text: string): number[] {
  const out: number[] = [];
  for (const ch of text.normalize('NFC')) {
    const sub = SUBST[ch];
    const chars = sub ?? ch;
    for (const c of chars) {
      const cp = c.codePointAt(0)!;
      let code: number | null = null;
      if (cp === 0x09 || cp === 0x0a || cp === 0x0d) code = 0x20;
      else if (cp >= 0x20 && cp < 0x7f) code = cp;
      else if (cp >= 0xa0 && cp <= 0xff) code = cp === 0xa0 ? 0x20 : cp === 0xad ? 0x2d : cp;
      else if (WIN_ANSI[cp] != null) code = WIN_ANSI[cp];
      if (code == null) continue;
      if (code === 0x20 && (out.length === 0 || out[out.length - 1] === 0x20)) continue;
      out.push(code);
    }
  }
  while (out.length && out[out.length - 1] === 0x20) out.pop();
  return out;
}

/** WinAnsi code of one UTF-16 unit of printable text (see {@link printable}), else null. */
function ansiCode(c: number): number | null {
  if ((c >= 0x20 && c < 0x7f) || (c >= 0xa0 && c <= 0xff)) return c;
  return WIN_ANSI[c] ?? null;
}

/** Text as it will be printed (emoji etc. removed). */
export function printable(text: string): string {
  return String.fromCharCode(...winAnsi(text)).replace(/[\x80-\x9f]/g, c => {
    const code = c.charCodeAt(0);
    const uni = Object.keys(WIN_ANSI).find(k => WIN_ANSI[+k] === code);
    return uni ? String.fromCodePoint(+uni) : '';
  });
}

const n = (v: number) => {
  const s = (Math.round(v * 100) / 100).toFixed(2);
  return s.replace(/\.?0+$/, '') || '0';
};
const rgb = (c: Rgb) => c.map(v => n(v / 255)).join(' ');

export class PdfWriter {
  /** A4 portrait in points. */
  readonly width = 595.28;
  readonly height = 841.89;
  private readonly pages: Page[] = [];
  private readonly images: PdfImage[] = [];
  private current = -1;

  get pageCount(): number { return this.pages.length; }

  addPage(): void {
    this.pages.push({ ops: [], links: [], images: new Set() });
    this.current = this.pages.length - 1;
  }

  /** Continue on an earlier page (e.g. for the page numbers once the count is known). */
  setPage(index: number): void {
    if (index >= 0 && index < this.pages.length) this.current = index;
  }

  private get page(): Page {
    if (this.current < 0) this.addPage();
    return this.pages[this.current];
  }

  textWidth(text: string, size: number, bold = false): number {
    const w = bold ? HELVETICA_BOLD : HELVETICA;
    return winAnsi(text).reduce((s, c) => s + (w[c - 32] ?? 556), 0) * size / 1000;
  }

  /** Width (points) of each character of printable text ({@link printable}: one WinAnsi code per character). */
  private charWidths(s: string, size: number, bold: boolean): number[] {
    const table = bold ? HELVETICA_BOLD : HELVETICA;
    const out = new Array<number>(s.length);
    for (let i = 0; i < s.length; i++) {
      const code = ansiCode(s.charCodeAt(i));
      out[i] = code == null ? 0 : (table[code - 32] ?? 556) * size / 1000;
    }
    return out;
  }

  /**
   * Lines of at most `width` (word wrap; a word longer than a line is broken). Paragraphs (\n) are kept.
   * Linear in the text: each character is measured once (a 3000-character link wraps in well under a ms).
   */
  wrap(text: string, width: number, size: number, bold = false): string[] {
    const lines: string[] = [];
    const space = this.charWidths(' ', size, bold)[0];
    for (const para of text.split(/\r?\n/)) {
      const words = printable(para).split(' ').filter(w => w !== '');
      if (!words.length) { lines.push(''); continue; }
      let line = '', lineW = 0;
      for (const word of words) {
        const cw = this.charWidths(word, size, bold);
        let start = 0;
        let rest = cw.reduce((a, b) => a + b, 0);
        while (rest > width) {
          // break an overlong word (a URL) at the last character that still fits on the line
          const room = line ? width - lineW - space : width;
          let k = start, acc = 0;
          while (k < cw.length && acc + cw[k] <= room) acc += cw[k++];
          let count = k - start;
          if (count < 1) { count = 1; acc = cw[start]; }
          if (line && (room < size * 2 || count <= 1)) { lines.push(line); line = ''; lineW = 0; continue; }
          const part = word.slice(start, start + count);
          lines.push(line ? line + ' ' + part : part);
          line = '';
          lineW = 0;
          start += count;
          rest -= acc;
        }
        const tail = start ? word.slice(start) : word;
        if (line && lineW + space + rest > width) { lines.push(line); line = tail; lineW = rest; }
        else if (line) { line += ' ' + tail; lineW += space + rest; }
        else { line = tail; lineW = rest; }
      }
      lines.push(line);
    }
    return lines;
  }

  /** `text` as printed, cut to `width` with "…" if it is wider (`atWord`: preferably at a word boundary). */
  fit(text: string, width: number, size: number, bold = false, atWord = false): string {
    const s = printable(text);
    const cw = this.charWidths(s, size, bold);
    if (cw.reduce((a, b) => a + b, 0) <= width) return s;
    const ell = this.charWidths('…', size, bold)[0];
    let k = 0, acc = 0;
    while (k < cw.length && acc + cw[k] + ell <= width) acc += cw[k++];
    let cut = s.slice(0, k);
    if (atWord) {
      const sp = cut.lastIndexOf(' ');
      if (sp > k * 0.6) cut = cut.slice(0, sp);
    }
    return cut.replace(/\s+$/, '') + '…';
  }

  /** Text whose baseline is at `y`. */
  text(x: number, y: number, text: string, opts: TextOpts = {}): void {
    const size = opts.size ?? 10;
    const bold = !!opts.bold;
    const codes = winAnsi(text);
    if (!codes.length) return;
    const w = this.textWidth(text, size, bold);
    const left = opts.align === 'center' ? x - w / 2 : opts.align === 'right' ? x - w : x;
    const hex = codes.map(c => c.toString(16).padStart(2, '0')).join('');
    this.page.ops.push(`BT /${bold ? 'F2' : 'F1'} ${n(size)} Tf ${rgb(opts.color ?? [0, 0, 0])} rg ${n(left)} ${n(this.height - y)} Td <${hex}> Tj ET`);
  }

  line(x1: number, y1: number, x2: number, y2: number, color: Rgb = [0, 0, 0], width = 0.5, dash: number[] = []): void {
    this.page.ops.push(`q ${n(width)} w ${rgb(color)} RG [${dash.map(n).join(' ')}] 0 d 1 J `
      + `${n(x1)} ${n(this.height - y1)} m ${n(x2)} ${n(this.height - y2)} l S Q`);
  }

  rect(x: number, y: number, w: number, h: number, fill: Rgb | null, stroke: Rgb | null = null, width = 0.5): void {
    const op = fill && stroke ? 'B' : fill ? 'f' : 'S';
    this.page.ops.push(`q ${fill ? rgb(fill) + ' rg ' : ''}${stroke ? rgb(stroke) + ' RG ' + n(width) + ' w ' : ''}`
      + `${n(x)} ${n(this.height - y - h)} ${n(w)} ${n(h)} re ${op} Q`);
  }

  /** Register a JPEG (its bytes, as from canvas.toDataURL('image/jpeg')) for drawImage. */
  addJpeg(bytes: Uint8Array, w: number, h: number): PdfImage {
    const img: PdfImage = { id: this.images.length + 1, w, h, bytes };
    this.images.push(img);
    return img;
  }

  drawImage(img: PdfImage, x: number, y: number, w: number, h: number): void {
    this.page.images.add(img.id);
    this.page.ops.push(`q ${n(w)} 0 0 ${n(h)} ${n(x)} ${n(this.height - y - h)} cm /Im${img.id} Do Q`);
  }

  /** A clickable area (top-left x / y, width, height) that opens `uri`. */
  link(x: number, y: number, w: number, h: number, uri: string): void {
    this.page.links.push({ rect: [x, this.height - y - h, x + w, this.height - y], uri });
  }

  /** The PDF file. `title`: the document title (Info dictionary). */
  toBytes(title = ''): Uint8Array {
    const chunks: Uint8Array[] = [];
    let length = 0;
    const offsets: number[] = [];
    const push = (b: Uint8Array) => { chunks.push(b); length += b.length; };
    const str = (s: string) => push(latin1(s));
    const obj = (id: number, body: () => void) => { offsets[id] = length; str(`${id} 0 obj\n`); body(); str('\nendobj\n'); };

    // object ids: 1 catalog, 2 pages, 3 / 4 fonts, 5 info, then images, then per page: page, content, links
    let next = 6;
    const imageIds = this.images.map(() => next++);
    const pageIds = this.pages.map(p => {
      const page = next++, content = next++;
      const links = p.links.map(() => next++);
      return { page, content, links };
    });

    push(new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34, 0x0a, 0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));   // %PDF-1.4 + binary marker
    obj(1, () => str('<< /Type /Catalog /Pages 2 0 R >>'));
    obj(2, () => str(`<< /Type /Pages /Kids [${pageIds.map(p => `${p.page} 0 R`).join(' ')}] /Count ${pageIds.length} >>`));
    obj(3, () => str('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'));
    obj(4, () => str('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>'));
    obj(5, () => str(`<< /Title ${utf16Hex(title)} /Producer (eg.blonkel.com) /CreationDate (${pdfDate(new Date())}) >>`));
    this.images.forEach((img, i) => obj(imageIds[i], () => {
      str(`<< /Type /XObject /Subtype /Image /Width ${img.w} /Height ${img.h} /ColorSpace /DeviceRGB `
        + `/BitsPerComponent 8 /Filter /DCTDecode /Length ${img.bytes.length} >>\nstream\n`);
      push(img.bytes);
      str('\nendstream');
    }));
    this.pages.forEach((p, i) => {
      const ids = pageIds[i];
      // only the images this page draws
      const xobjects = this.images.flatMap((img, k) => p.images.has(img.id) ? [`/Im${img.id} ${imageIds[k]} 0 R`] : []).join(' ');
      obj(ids.page, () => str(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(this.width)} ${n(this.height)}] `
        + `/Resources << /Font << /F1 3 0 R /F2 4 0 R >>${xobjects ? ` /XObject << ${xobjects} >>` : ''} >> `
        + `/Contents ${ids.content} 0 R${ids.links.length ? ` /Annots [${ids.links.map(l => `${l} 0 R`).join(' ')}]` : ''} >>`));
      const content = latin1(p.ops.join('\n'));
      obj(ids.content, () => { str(`<< /Length ${content.length} >>\nstream\n`); push(content); str('\nendstream'); });
      p.links.forEach((l, k) => obj(ids.links[k], () => str(
        `<< /Type /Annot /Subtype /Link /Rect [${l.rect.map(n).join(' ')}] /Border [0 0 0] `
        + `/A << /Type /Action /S /URI /URI ${pdfString(l.uri)} >> >>`)));
    });
    const xref = length;
    const count = next;
    let table = `xref\n0 ${count}\n0000000000 65535 f \n`;
    for (let id = 1; id < count; id++) table += `${String(offsets[id] ?? 0).padStart(10, '0')} 00000 n \n`;
    str(table);
    str(`trailer\n<< /Size ${count} /Root 1 0 R /Info 5 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

    const out = new Uint8Array(length);
    let at = 0;
    for (const c of chunks) { out.set(c, at); at += c.length; }
    return out;
  }
}

/** Bytes of an ASCII / Latin-1 string (the PDF syntax; text is hex-encoded beforehand). */
function latin1(s: string): Uint8Array {
  const b = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff;
  return b;
}

/** A literal string with the special characters escaped; non-ASCII as octal escapes. */
function pdfString(s: string): string {
  let out = '(';
  for (const c of s) {
    const cp = c.codePointAt(0)!;
    if (c === '(' || c === ')' || c === '\\') out += '\\' + c;
    else if (cp < 0x20 || cp > 0x7e) {
      for (const byte of new TextEncoder().encode(c)) out += '\\' + byte.toString(8).padStart(3, '0');
    } else out += c;
  }
  return out + ')';
}

/** A text string (Info dictionary) as UTF-16BE hex with BOM. */
function utf16Hex(s: string): string {
  let hex = 'FEFF';
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return `<${hex}>`;
}

function pdfDate(d: Date): string {
  const p = (v: number) => String(v).padStart(2, '0');
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  return `D:${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
    + `${sign}${p(Math.floor(Math.abs(off) / 60))}'${p(Math.abs(off) % 60)}'`;
}

/** A data URL's bytes (base64). */
export function dataUrlBytes(url: string): Uint8Array {
  const b64 = url.slice(url.indexOf(',') + 1);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
